import InfoIcon from '../../assets/info-svg.vue'
import Camera from '../../assets/camera.vue'
import Pencil from '../../assets/pencil.vue'
import Arrow from '../../assets/arrow.vue'
import Upload from '../../assets/upload.vue'
import Delete from '../../assets/delete-svg.vue'
import { onMounted, ref, watch, onUnmounted } from 'vue'
import { Toaster } from 'src/utils/helper/toaster'
import i18n from 'src/assets/i18n/i18n'
import CropperImage from 'src/components/general/cropper-image/index.vue'
import { MdTypography, MdButton, MdIconFavoriteSolid, MdLink, MdIconExport, MdIconEmptyTrash, MdIconEdit} from '@antiqua-dls/vue-components'
import AnimationProcessing from 'components/animations/processing'
import { useStore } from 'vuex';
const { t } = i18n().global

export default {
    components: {
        InfoIcon,
        Camera,
        Pencil,
        Arrow,
        Upload,
        Delete,
        CropperImage,
        AnimationProcessing,
        MdTypography,
        MdButton,
        MdIconFavoriteSolid,
        MdLink,
        MdIconExport,
        MdIconEmptyTrash,
        MdIconEdit
    },

    props: {
        name: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default: ''
        },
        imgBanner: {
            type: String,
            default: ''
        },
        imgLogo: {
            type: String,
            default: ''
        },
        logoProcessing: {
            type: Boolean,
            default: false
        },
        bannerProcessing: {
            type: Boolean,
            default: false
        },
        getAvScanStatus: {
            type: String,
            default: {status: '', name: ''}
        },
    },

    emits: ['update:imageUpload', 'delete:imageBanner', 'show:OverlayInfo'],

    setup(props, { emit }) {
        const files = ref([]);
        const loadingBanner = ref(false);
        const loadingLogo = ref(false);
        const resourceLocBanner = ref('')
        const resourceLocLogo = ref('')
        const showBannerDropdown = ref(false)
        const showLogoDropdown = ref(false)
        const bannerList = ref(null);
        const bannerEdit = ref(null);
        const logoEdit = ref(null);
        const logoList = ref(null);
        const imageCropper = ref(null);
        const imageCropperName = ref('');
        const showImageCropper = ref(false);
        const setPriority = ref(null);
        const defaultBannerUrl = ref(require('../../assets/default-banner.svg').default);
        const store = useStore()

        const toggleBannerDropdown = () => {
            showBannerDropdown.value = !showBannerDropdown.value
            showLogoDropdown.value = false
        }

        const toggleLogoDropdown = () => {
            showLogoDropdown.value = !showLogoDropdown.value
            showBannerDropdown.value = false
        }

        const upload = async (e, priority) => {
            if (priority === 1) { 
                loadingLogo.value = true
            }
            if (priority === 2) { 
                loadingBanner.value = true
            }
            const allowedFormats = ['image/png', 'image/jpeg', 'image/jpg']

            const filesArray = Array.from(e.target.files)
            const payload = {
                file: filesArray[0],
                foundryId: props.id,
                priority
            }
            setPriority.value = priority
            const apiPromises = filesArray.map(file => processFile(file, payload, allowedFormats, priority))

            await Promise.allSettled(apiPromises)
            resetValues()
        }

        // Helper function to handle image load logic
        const handleImageLoad = (img, file, allowedFormats, priority, e, payload, resolve) => {
            if (!isFileFormatValid(file, allowedFormats)) {
                notifyErrorAndResolve(t('pages.profile.foundry_profile.image_format_error'), resolve)
                return
            }

            if (!isFileSizeValid(file, priority)) {
                resolve()
                return
            }

            if (!isResolutionValid(img, priority)) {
                resolve()
                return
            }
            uploadImage(e.target.result, payload)
            resolve()
        }

        // Helper function to handle reader onload
        const handleReaderOnLoad = (file, allowedFormats, priority, payload, resolve) => (e) => {
            const img = new Image()
            img.src = e.target.result
            img.onload = () => {
                handleImageLoad(img, file, allowedFormats, priority, e, payload, resolve)
            }
        }

        const processFile = async (file, payload, allowedFormats, priority) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)

            try {
                await new Promise((resolve) => {
                    if (!file.type.startsWith('image/')) {
                        notifyErrorAndResolve(t('pages.profile.foundry_profile.image_format_error'), resolve)
                        return
                    }

                    reader.onload = handleReaderOnLoad(file, allowedFormats, priority, payload, resolve)
                })

            } catch {
                resetValues()
                // 'resolve' is not defined here, so just notify error
                Toaster.notifyError(t('pages.profile.foundry_profile.catcherror'))
                files.value = []
            }
        }

        const isFileFormatValid = (file, allowedFormats) => {
            return allowedFormats.some(format => file.type.includes(format))
        }

        const isFileSizeValid = (file, priority) => {
            const fileSizeInMB = file.size / (1024 * 1024);
            let showError = false

            if (priority === 2 && fileSizeInMB > 8) {
                Toaster.notifyError(t('pages.profile.foundry_profile.bannerfilesizeinvalid'));
                showError = true
            } else if (priority === 1 && fileSizeInMB > 4) {
                Toaster.notifyError(t('pages.profile.foundry_profile.logofilesizeinvalid'));
                showError = true
            }
            if (showError) {
                resetValues()
                return false;
            }
            return true;
        }

        const isResolutionValid = (img, priority) => {
            const isBannerFileResolutionInValid = img.width < 1200 || img.height < 300
            const isLogoFileResolutionInValid = img.width < 256 || img.height < 256

            if (isBannerFileResolutionInValid && priority === 2) {
                Toaster.notifyError(t('pages.profile.foundry_profile.bannerfileresolutioninvalid'))
                resetValues()
                return false;
            }

            if (isLogoFileResolutionInValid && priority === 1) {
                Toaster.notifyError(t('pages.profile.foundry_profile.logofileresolutioninvalid'))
                resetValues()
                return false;
            }
            return true;
        }

        const notifyErrorAndResolve = (message, resolve) => {
            Toaster.notifyError(message)
            resetValues()
            resolve()
        }

        const resetValues = () => {
            loadingLogo.value = false
            loadingBanner.value = false
            showBannerDropdown.value = false
            showLogoDropdown.value = false
            files.value = []
        }
        const uploadImage = (result, payload) => {
            loadingLogo.value = false
            loadingBanner.value = false
            showBannerDropdown.value = false
            showLogoDropdown.value = false
            showImageCropper.value = true
            imageCropper.value = result
            imageCropperName.value = payload?.file?.name;
        }

        const hideCropperModal = (e) => {
            if (e) {
                e.preventDefault()
            }
            showImageCropper.value = false
        }

        const setUploadedImage = (croppedImage) => {
            showImageCropper.value = false
            const payload = {
                file: croppedImage,
                foundryId: props.id,
                priority: setPriority.value
            }
            emit('update:imageUpload', payload)
        }

        const emitToDelete = (option) => {
            emit('delete:imageBanner', option)
            showBannerDropdown.value = false
            showLogoDropdown.value = false
        }
        
        const closeBannerDropdownOnOutsideClick = (event) => {
            const isBannerDropdown = bannerList.value?.contains(event.target)
            const isBannerEdit = bannerEdit.value?.contains(event.target)
            if (!isBannerDropdown && !isBannerEdit) {
                showBannerDropdown.value = false
            }
        }

        const closeLogoDropdownOnOutsideClick = (event) => {
            const isLogoDropdown = logoList.value?.contains(event.target)
            const isLogoEdit = logoEdit.value?.contains(event.target)
            if (!isLogoDropdown && !isLogoEdit) {
                showLogoDropdown.value = false
            }
        }

        const showOverlayInfoFunc = (sec) => {
            emit('show:OverlayInfo', sec)
        }

        watch(
            () => props.imgBanner,
            (newValue) => {
                resourceLocBanner.value = newValue
            },
            { immediate: true }
        )

        watch(
            () => props.imgLogo,
            (newValue) => {
                resourceLocLogo.value = newValue
            },
            { immediate: true }
        )

        watch(
            () => props.bannerProcessing,
            (newValue) => {
                loadingBanner.value = newValue
            },
            { immediate: true }
        )

        watch(
            () => props.logoProcessing,
            (newValue) => {
                loadingLogo.value = newValue
            },
            { immediate: true }
        )

        onMounted(() => {
            document.addEventListener('click', closeBannerDropdownOnOutsideClick);
            document.addEventListener('click', closeLogoDropdownOnOutsideClick);
        })

        onUnmounted(() => {
            document.removeEventListener('click', closeBannerDropdownOnOutsideClick);
            document.removeEventListener('click', closeLogoDropdownOnOutsideClick);
            store.commit('foundrySupport/setAvScanStatus', {status: '', name: ''})
        })

        return {
            setPriority,
            files,
            loadingLogo,
            loadingBanner,
            resourceLocBanner,
            resourceLocLogo,
            showBannerDropdown,
            showLogoDropdown,
            defaultBannerUrl,
            bannerList,
            bannerEdit,
            logoEdit,
            logoList,
            showOverlayInfoFunc,
            imageCropper,
            imageCropperName,
            showImageCropper,
            notifyErrorAndResolve,
            isFileFormatValid,
            isResolutionValid,
            isFileSizeValid,
            hideCropperModal,
            upload,
            setUploadedImage,
            toggleBannerDropdown,
            toggleLogoDropdown,
            emitToDelete,
            uploadImage,
            closeBannerDropdownOnOutsideClick,
            closeLogoDropdownOnOutsideClick
        }
    }
}